import { ReactNode, forwardRef } from 'react';
import {
  SidebarMenuSection,
  SidebarMenuSectionProps,
} from './SidebarMenuSection';
import { SidebarMenuHeader } from './SidebarMenuHeader';
import { SidebarMenuBody } from './SidebarMenuBody';
import { SidebarMenuFooter, SidebarMenuFooterProps } from './SidebarMenuFooter';
import { SidebarMenuEmptyContent } from './SidebarMenuEmptyContent';
import {
  SidebarMenuWrapper,
  SidebarMenuWrapperProps,
} from './SidebarMenuWrapper';
import { pick } from 'lodash';
import { IconName } from '@ardoq/icons';

type SectionOption = Omit<SidebarMenuSectionProps, 'children'> & {
  body?: ReactNode;
};

export type SidebarMenuProps = {
  headerTitle?: string;
  headerTitleTestId?: string;
  headerLeftIconName?: IconName;
  headerLeftContent?: ReactNode;
  headerRightContent?: ReactNode;
  sections?: SectionOption[];
  onCloseClick?: () => void;
  headerBody?: ReactNode;
  children?: ReactNode;
  footerHintMessage?: string;
  footerErrorMessage?: string | null;
  emptyContentMessage?: string;
  closeButtonLabel?: string;
  closeButtonAriaLabel?: string;
} & SidebarMenuWrapperProps &
  Pick<
    SidebarMenuFooterProps,
    | 'primaryButtonLabel'
    | 'primaryButtonClickId'
    | 'onPrimaryButtonClick'
    | 'isPrimaryButtonDisabled'
    | 'dangerButtonLabel'
    | 'dangerButtonClickId'
    | 'onDangerButtonClick'
    | 'isDangerButtonDisabled'
    | 'footerContent'
  >;

export const SidebarMenu = forwardRef<HTMLFormElement, SidebarMenuProps>(
  (
    {
      // header
      headerTitle,
      headerTitleTestId,
      headerBody,
      headerLeftIconName,
      headerLeftContent,
      headerRightContent,
      onCloseClick,

      // body
      sections = [],
      children,

      footerHintMessage,
      footerErrorMessage,
      emptyContentMessage,

      footerContent,

      // Page body menu options
      isPageBodyMenu,
      isRightContent,
      isResizable,
      isFullWidth,
      closeButtonLabel,
      closeButtonAriaLabel,
      ...props
    },
    ref
  ) => {
    const footerProps = pick(
      props,
      'primaryButtonLabel',
      'primaryButtonClickId',
      'onPrimaryButtonClick',
      'isPrimaryButtonDisabled',
      'dangerButtonLabel',
      'dangerButtonClickId',
      'onDangerButtonClick',
      'isDangerButtonDisabled'
    );
    return (
      <SidebarMenuWrapper
        $isPageBodyMenu={isPageBodyMenu}
        $isRightContent={isRightContent}
        $isResizable={isResizable}
        $isFullWidth={isFullWidth}
        ref={ref}
        onSubmit={
          !footerProps.isPrimaryButtonDisabled &&
          footerProps.onPrimaryButtonClick
            ? footerProps.onPrimaryButtonClick
            : e => {
                e.preventDefault();
                e.stopPropagation();
              }
        }
      >
        {(headerTitle || headerBody || onCloseClick) && (
          <SidebarMenuHeader
            dataTestId={headerTitleTestId}
            title={headerTitle}
            onCloseClick={onCloseClick}
            leftIconName={headerLeftIconName}
            leftContent={headerLeftContent}
            rightContent={headerRightContent}
            closeButtonLabel={closeButtonLabel}
            closeButtonAriaLabel={closeButtonAriaLabel}
          >
            {headerBody}
          </SidebarMenuHeader>
        )}
        <SidebarMenuBody>
          {sections.map(({ body, ...sectionProps }, index) => (
            <SidebarMenuSection key={index} {...sectionProps}>
              {body}
            </SidebarMenuSection>
          ))}
          {!sections.length && !children && emptyContentMessage && (
            <SidebarMenuEmptyContent>
              {emptyContentMessage}
            </SidebarMenuEmptyContent>
          )}
          {children}
        </SidebarMenuBody>
        {(footerContent ||
          footerHintMessage ||
          footerProps.primaryButtonLabel ||
          footerProps.dangerButtonLabel) && (
          <SidebarMenuFooter
            {...footerProps}
            footerContent={footerContent}
            hintMessage={footerHintMessage}
            errorMessage={footerErrorMessage}
          />
        )}
      </SidebarMenuWrapper>
    );
  }
);
